import * as React from "react";

import { getCitationColor, type CitationColor } from "../citations/colors";

interface CitationUnderlineProps {
  /** The UUID from `<u id="…">`. Used to look up the full Citation. */
  id: string;
  /** Optional `data-datasource` attribute (e.g. `FACTSET_FUNDAMENTALS`). */
  dataSource?: string;
  /** Optional `data-type` attribute. Currently only `calculation` is special-cased. */
  dataType?: string;
  /** The cited value as plain text - rendered inside the underline span. */
  children: React.ReactNode;
  /** Click handler. Receives the underline id so the owner can resolve the citation. */
  onClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Override the color resolver. Defaults to the bundled
   * datasource -> CitationColor mapping.
   */
  resolveColor?: (dataSource: string | undefined | null, options?: { id?: string | null; dataType?: string | null }) => CitationColor;
}

/**
 * Inline `<u>` rendered with a data-source colored underline, mirroring
 * finster-chat's underline component (without the source-information
 * drawer wiring - that's owned by `ChatView`). Always renders as a
 * clickable span; if there's no `onClick` it still appears underlined
 * but does nothing on click.
 */
export const CitationUnderline: React.FC<CitationUnderlineProps> = ({
  id,
  dataSource,
  dataType,
  children,
  onClick,
  resolveColor,
}) => {
  const color = (resolveColor ?? getCitationColor)(dataSource, { id, dataType });
  const isCalculation = dataType === "calculation" || dataSource === "CALCULATION";

  const handleClick = onClick
    ? (event: React.MouseEvent<HTMLElement>) => onClick(id, event)
    : undefined;
  const handleKeyDown = onClick
    ? (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(id, event as unknown as React.MouseEvent<HTMLElement>);
        }
      }
    : undefined;

  return (
    <span
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      data-citation-id={id}
      data-citation-datasource={dataSource}
      data-citation-type={dataType}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        cursor: onClick ? "pointer" : "default",
        textDecorationLine: "underline",
        textDecorationColor: color,
        textDecorationStyle: isCalculation ? "dotted" : "solid",
        textUnderlineOffset: 3,
        textDecorationThickness: 2,
        textDecorationSkipInk: "none",
        ...(isCalculation ? { color } : undefined),
      }}
    >
      {children}
    </span>
  );
};
